import React, { useState } from 'react';
import { Alert, ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { Screen } from '../shared/Screen';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { colors, fonts, spacing } from '../theme';

export function SignInScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signIn, signInWithGoogle, loading } = useAuth();

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (!email || !password) {
      const msg = t('auth.signIn.requiredFieldsMessage');
      setErrorMessage(msg);
      Alert.alert(t('auth.signIn.requiredFieldsTitle'), msg);
      return;
    }
    if (password.length < 8) {
      const msg = t('auth.signIn.shortPasswordMessage');
      setErrorMessage(msg);
      Alert.alert(t('auth.signIn.shortPasswordTitle'), msg);
      return;
    }
    try {
      await signIn(email, password);
    } catch (error) {
      const msg = (error as Error).message;
      setErrorMessage(msg);
      Alert.alert(t('auth.signIn.errorTitle', { defaultValue: 'Erro ao entrar' }), msg);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      const msg = (error as Error).message;
      setErrorMessage(msg);
      Alert.alert(t('auth.signIn.googleErrorTitle', { defaultValue: 'Erro ao entrar com Google' }), msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Screen>
      <Heading>{t('auth.signIn.title')}</Heading>
      <Button
        variant="line"
        title={t('auth.signIn.googleButton')}
        onPress={handleGoogleSignIn}
        loading={googleLoading}
        disabled={loading || googleLoading}
      />
      <View style={styles.dividerContainer}>
        <View style={styles.dividerLine} />
        <Text style={styles.dividerText}>{t('auth.signIn.divider')}</Text>
        <View style={styles.dividerLine} />
      </View>
      <TextField
        placeholder={t('auth.signIn.emailPlaceholder')}
        value={email}
        onChangeText={(text) => {
          setEmail(text);
          setErrorMessage(null);
        }}
        autoCapitalize="none"
        keyboardType="email-address"
      />
      <TextField
        placeholder={t('auth.signIn.passwordPlaceholder')}
        value={password}
        onChangeText={(text) => {
          setPassword(text);
          setErrorMessage(null);
        }}
        secureTextEntry
      />
      {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}
      {loading ? (
        <ActivityIndicator />
      ) : (
        <Button title={t('auth.signIn.submit')} onPress={handleSubmit} disabled={googleLoading} />
      )}
      <Button
        variant="ghost"
        title={t('auth.signIn.forgotPassword')}
        onPress={() => router.push('/(auth)/recover')}
      />
      <Button
        variant="ghost"
        title={t('auth.signIn.createAccount')}
        onPress={() => router.push('/(auth)/signup')}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  errorText: {
    fontFamily: fonts.body,
    fontSize: 14,
    color: colors.clay,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.sm,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.line,
  },
  dividerText: {
    marginHorizontal: spacing.sm,
    fontFamily: fonts.bodyMedium,
    fontSize: 13,
    color: colors.thread,
  },
});
